
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Link } from 'lucide-react';
import { useSocialAccounts } from '@/hooks/useSocialAccounts';
import { useFacebookGroups } from '@/hooks/useFacebookGroups';
import { useToast } from '@/hooks/use-toast';
import { ConnectedSocialAccount } from './social/ConnectedSocialAccount';
import { AddSocialAccountButtons } from './social/AddSocialAccountButtons';
import { FacebookSuccessMessage } from './social/FacebookSuccessMessage';

export const SocialAccountsManager = () => {
  const { accounts, loading, removeAccount, refetch } = useSocialAccounts();
  const { groups: facebookGroups, loading: groupsLoading, refetch: refetchGroups } = useFacebookGroups();
  const { toast } = useToast();

  // Handle OAuth callback results
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const facebookConnected = urlParams.get('facebook_connected');
    const facebookError = urlParams.get('facebook_error');
    const groupsLoaded = urlParams.get('groups_loaded');

    if (facebookConnected === 'true') {
      toast({
        title: "Success",
        description: `Facebook account connected successfully! ${groupsLoaded ? `Found ${groupsLoaded} groups you can post to.` : ''}`,
      });
      refetch();
      refetchGroups();
      // Clean up URL parameters
      window.history.replaceState({}, '', window.location.pathname);
    }

    if (facebookError) {
      toast({
        title: "Error",
        description: `Failed to connect Facebook: ${decodeURIComponent(facebookError)}`,
        variant: "destructive"
      });
      // Clean up URL parameters
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [toast, refetch, refetchGroups]);

  const facebookAccount = accounts.find(account => account.platform === 'facebook');
  const connectedPlatforms = accounts.map(account => account.platform);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Social Media Accounts</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4 text-gray-500">Loading...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-2">
        <Link size={16} />
        <span className="text-sm font-medium">Connected Accounts</span>
      </div>
      
      {accounts.length === 0 ? (
        <div className="text-center py-3 text-gray-500">
          <p className="text-xs">No accounts connected</p>
        </div>
      ) : (
        <div className="space-y-2">
          {accounts.map((account) => (
            <ConnectedSocialAccount
              key={account.id}
              account={account}
              facebookGroups={account.platform === 'facebook' ? facebookGroups : []}
              groupsLoading={groupsLoading}
              onRemoveAccount={removeAccount}
            />
          ))}
        </div>
      )}

      <AddSocialAccountButtons
        connectedPlatforms={connectedPlatforms}
        onRefetch={refetch}
        onRefetchGroups={refetchGroups}
      />
      
      <FacebookSuccessMessage hasConnectedFacebook={!!facebookAccount} />
    </div>
  );
};
